import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../../components/about/AboutSideNav';

// ===== Activities (EN mirror) — full rebuild, Implementer A scope =====
// Design language mirrors the JA activities page (navy #1a2942 / brass #a38e65 / BTN-08 / gold accent
//   headings / editorial rules / navy panels). Section types are all distinct within the page and
//   reuse the learned catalog grammar (N02 split / N04 stacked cards / N05 three-column) plus two new
//   variants (large-font list table, navy "promise" panel). Facts sourced from the same canon as JA.
//   No invented figures. EN pages contain no Japanese characters (SEO rule 1-3).

export default function EnAboutActivitiesPage() {
  useEffect(() => {
    document.title = 'Activities | Japan Butler Association';

    const setMeta = (name: string, content: string) => {
      let m = document.querySelector('meta[name="' + name + '"]');
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', name); document.head.appendChild(m); }
      m.setAttribute('content', content);
    };
    setMeta('description', 'The activities of the Japan Butler Association: training and development, a three-tier certification system, seminars, international exchange and publishing, and the confidentiality and screening that safeguard quality since 1890.');

    const setLink = (rel: string, href: string, hreflang?: string) => {
      const sel = hreflang ? 'link[rel="' + rel + '"][hreflang="' + hreflang + '"]' : 'link[rel="' + rel + '"]';
      let l = document.querySelector(sel);
      if (!l) { l = document.createElement('link'); l.setAttribute('rel', rel); if (hreflang) l.setAttribute('hreflang', hreflang); document.head.appendChild(l); }
      l.setAttribute('href', href);
    };
    setLink('canonical', 'https://butler-association.jp/en/about/activities');
    setLink('alternate', 'https://butler-association.jp/about/activities', 'ja');
    setLink('alternate', 'https://butler-association.jp/en/about/activities', 'en');
    setLink('alternate', 'https://butler-association.jp/about/activities', 'x-default');

    document.documentElement.lang = 'en';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us', href: '/en/about' },
    { label: 'Activities' }
  ];

  const aboutNav = [
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const activityPillars = [
    { no: '01', name: 'Training & Development', lead: 'Systematic programs cultivate high-quality butlers, from fundamentals to advanced technique.', anchor: '#training' },
    { no: '02', name: 'Certification System', lead: 'A three-tier ladder — Associate, Professional, Master — objectively certifies skill.', anchor: '#certification' },
    { no: '03', name: 'Sharing Our Knowledge', lead: 'Seminars, international exchange, and publishing bring butler culture to society.', anchor: '#outreach' },
    { no: '04', name: 'Trust & Quality', lead: 'Confidentiality, screening, and clear accountability underpin everything we do.', anchor: '#trust' },
  ];

  const certifications = [
    { grade: 'Associate', ja: 'Associate Butler', img: '/images/generated/jba-still-shipdesk-01.webp', alt: 'A study desk with a sailing-ship model and a globe', desc: 'The first milestone, having mastered the fundamental skills, conduct, and manners of a butler. A systematic grounding in hospitality.' },
    { grade: 'Professional', ja: 'Professional Butler', img: '/images/generated/jba-interior-suits-02.webp', alt: "A wardrobe lined with butlers' formal attire", desc: 'A stage with specialist ability to support a client’s home and business in the field — dining, hospitality, and travel arrangements.' },
    { grade: 'Master', ja: 'Master Butler', img: '/images/generated/jba-still-roses-01.webp', alt: 'A corner of a salon with roses and an easel', desc: 'The highest tier, overseeing the running of an entire household and mentoring others — the pinnacle reached after roughly three years of development.' },
  ];

  const outreach = [
    { en: 'Seminars', ja: 'Seminars & Events', img: '/images/generated/jba-fs-002-about_activities.webp', alt: 'A business manner training session for a corporate seminar', desc: 'Practical seminars on leadership, hospitality, and communication for companies and individuals, bringing the conduct and mindset of the butler to the business world.' },
    { en: 'International', ja: 'International Exchange', img: '/images/generated/jba-fs-003-about_activities.webp', alt: 'A scene symbolizing an international network of butlers', desc: 'With a global outlook, we work to develop butlers who meet international standards, placing importance on our readiness to serve guests from overseas.' },
    { en: 'Publishing', ja: 'Publishing & Media', img: '/images/gen2/jba-publishing-media-materials-02.jpg', alt: 'Butler-culture publications and media-production materials reviewed for publishing and information outreach', desc: 'Through specialist publications and books, and the "Butler Bell" YouTube channel, we convey the present state of butler culture broadly to society.' },
  ];

  const trustPillars = [
    { en: 'Confidentiality', ja: 'Two-way Confidentiality', desc: 'A mutual NDA is signed between the association, the butler, and the client. Because this work touches the most private parts of life, confidentiality is firmly protected.' },
    { en: 'Screening', ja: 'Anti-social Force Screening', desc: 'Both butlers and clients undergo screening against anti-social forces, maintaining a compliance standard robust enough for rigorous due diligence.' },
    { en: 'Accountability', ja: 'Not Dispatch, but Responsibility', desc: 'Not labor dispatch, but a referral and commission model led by the association — which stands between the parties and takes responsibility from quality control to issue resolution.' },
  ];

  return (
    <Layout>
      {/* Hero — full-width billboard */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/salon0926/184924_0.jpg"
          alt="A butler holding a tray in the dining room of the butler salon"
          className="absolute inset-0 w-full h-full object-cover object-right"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Our Activities</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">To cultivate, certify,</span>
              <span className="block">share, and safeguard</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                The Japan Butler Association works to raise the professionalism of butlers and to pass their culture on to the next generation.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                Training that develops people, certification that proves skill, outreach that shares our knowledge, and the quiet mechanisms that safeguard quality shape our everyday work.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side nav + content */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/en/about/activities" eyebrow="About Us" heading="About Us" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* I. Overview — large-font list table (new variant 1) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Overview</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  Our activities, in <span className="text-[#a38e65]">four pillars</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  <div className="space-y-5">
                    <p>
                      Our work rests on four pillars.
                    </p>
                    <p>
                      First, training that develops people; second, certification that objectively proves their skill; third, outreach that shares that learning with society; and fourth, the trust and quality mechanisms that quietly support all of the above.
                    </p>
                  </div>
                  <div className="space-y-5">
                    <p>
                      These are not separate efforts but a single cycle: we certify the skills of those we train, return that learning to society through seminars and publishing, and let confidentiality and screening form the foundation beneath it all.
                    </p>
                    <p>
                      First, see the whole picture at a glance.
                    </p>
                  </div>
                </div>
                <div className="border-t-2 border-[#1a2942]">
                  {activityPillars.map((p) => (
                    <a
                      key={p.no}
                      href={p.anchor}
                      className="group flex items-baseline gap-4 sm:gap-7 border-b border-[#e2ddd2] py-6 hover:bg-[#f8f6f0] transition-colors duration-300 -mx-2 px-2"
                    >
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none shrink-0 w-9 sm:w-12">{p.no}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block jba-display font-medium text-[#1a2942] text-xl sm:text-2xl lg:text-[26px] leading-[1.4] tracking-[0.02em] group-hover:text-[#a38e65] transition-colors duration-300">{p.name}</span>
                        <span className="block font-['Noto_Serif_JP',serif] text-sm sm:text-[15px] text-[#4a4638] leading-[1.85] mt-1.5">{p.lead}</span>
                      </span>
                      <span className="hidden sm:block font-['Playfair_Display',serif] text-[#8f7a56] text-xl shrink-0 self-center transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
                    </a>
                  ))}
                </div>
              </section>

              {/* II. Training — split editorial (catalog N02) */}
              <section id="training" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Training</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  It all begins with developing people
                </h2>
                <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
                  <div>
                    <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                      <div className="space-y-5">
                        <p>
                          From fundamental skills to advanced technique, systematic training programs develop high-quality butlers.
                        </p>
                        <p>
                          Combining theory and practice, learners progress step by step — from the foundations of conduct, manners, and hospitality through to specialist areas such as dining and reception.
                        </p>
                      </div>
                      <div className="space-y-5">
                        <p>
                          The place of learning is our headquarters and "Butler Salon" in Odaiba. This base, which recreates a room of a medieval European castle, serves as the setting for hands-on practice and passes tradition on to the next generation.
                        </p>
                        <p>
                          After roughly three years of development, accomplished professionals step out into the field.
                        </p>
                      </div>
                    </div>
                    <dl className="mt-8 border-t border-[#e2ddd2]">
                      {[
                        { t: 'Fundamentals', d: 'The foundations of conduct, manners, protocol, and communication' },
                        { t: 'Specialist skills', d: 'Practical areas such as dining service, reception, and travel arrangements' },
                        { t: 'Hands-on practice', d: 'Practical training at the Butler Salon in Odaiba' },
                      ].map((row) => (
                        <div key={row.t} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                          <dt className="sm:w-[132px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em] pt-0.5">{row.t}</dt>
                          <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] leading-[1.85]">{row.d}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <img
                    src="/images/generated/jba-hands-notebook-03.webp"
                    alt="A butler's white-gloved hands taking notes"
                    className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30"
                  />
                </div>
              </section>

              {/* III. Certification — stacked split cards (catalog N04) */}
              <section id="certification" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Certification</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Proving skill in <span className="text-[#a38e65]">three tiers</span>
                </h2>
                <div className="space-y-5 mb-9">
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    We operate our own certification system that objectively evaluates and certifies a butler’s specialist knowledge and skill.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    Centered on the three tiers of Associate, Professional, and Master, it proves skill step by step and safeguards service quality.
                  </p>
                </div>
                <div className="space-y-6">
                  {certifications.map((c) => (
                    <div key={c.grade} className="grid sm:grid-cols-[0.4fr_0.6fr] border border-[#e2ddd2] overflow-hidden bg-white">
                      <img src={c.img} alt={c.alt} className="w-full h-52 sm:h-full min-h-[200px] object-cover object-top" />
                      <div className="p-7 sm:p-8 flex flex-col justify-center">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.08em] mb-1.5">{c.grade} Butler</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl lg:text-[26px] leading-[1.4] mb-3">{c.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{c.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* IV. Outreach — three-column card grid (catalog N05) */}
              <section id="outreach" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Outreach</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Sharing our knowledge beyond the association
                </h2>
                <div className="space-y-5 mb-9">
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    The skills and mindset of the butler are not meant to stay within the association.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    Through seminars to companies and individuals, international exchange to the world, and publishing to society at large, we share the value of butler culture across three channels.
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
                  {outreach.map((o) => (
                    <div key={o.ja} className="border border-[#e2ddd2] overflow-hidden bg-white flex flex-col">
                      <img src={o.img} alt={o.alt} className="w-full h-48 object-cover object-top" />
                      <div className="p-6 flex-1 flex flex-col">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1.5">{o.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{o.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{o.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* V. Trust & Quality — navy "promise" panel (new variant 2) */}
              <section id="trust" className="scroll-mt-28">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Trust &amp; Quality</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  So that you can entrust us with confidence
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-14 py-11 sm:py-13 lg:py-14">
                  <div className="space-y-5 mb-10">
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      A butler works at the most private heart of a home and a business. That is precisely why, behind the visible service, we uphold three mechanisms — confidentiality, screening, and accountability.
                    </p>
                    <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl">
                      Only when all three are in place does a relationship you can entrust with confidence hold true.
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-8 lg:gap-10 border-t border-[#a38e65]/40 pt-10">
                    {trustPillars.map((tp) => (
                      <div key={tp.en}>
                        <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">{tp.en}</div>
                        <h3 className="jba-display font-medium text-[#f8f6f0] text-lg lg:text-xl mb-3 leading-[1.5]">{tp.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">{tp.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* VI. Next Steps — navy CTA panel */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Explore our activities
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    From the details of our certification system to seminars for companies and individuals, there are many ways to engage with the association’s activities. Start with whichever theme interests you.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/en/qualifications/" className={BTN08_ON_NAVY}>View Certifications</a>
                    <a href="/en/seminars/" className={BTN08_ON_NAVY}>View Seminars</a>
                    <a href="/en/contact/" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
